import { Flex, Text, Spinner, Divider, DataTable, FormattedDateTime } from '@customerio/pluma-components/react';
import { createColumnHelper, type CellContext } from '@tanstack/table-core';
import plur from 'plur';
import type { SearchResult } from '@/services/auth/types';
import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { formatNumber } from '@/utils/number';
import { isEmptyString } from '@/utils/string';
import { MetadataRow } from './metadata-row';

// A quick peek, not the full dataset — the result links through to the full table.
const PREVIEW_ROWS = 5;
const PREVIEW_COLUMNS = 4;

type Props = { result: Extract<SearchResult, { kind: 'collection' }> };

// Record<string, string> (rather than the precise literal union) so a future backend import source
// still renders gracefully — it falls through to the raw value instead of becoming a type error.
// Mirrors collections/import.go: FromS3 / FromSheet / FromURL. An empty import_type is a
// manually-built collection, which has no source to show (the row is hidden).
const IMPORT_TYPE_LABELS: Record<string, string> = {
  s3: 'Amazon S3',
  sheet: 'Google Sheets',
  url: 'URL',
};

type CollectionItem = Record<string, unknown>;
type CollectionItemsResponse = { collection_items: CollectionItem[]; total: number };
const columnHelper = createColumnHelper<CollectionItem>();

function formatCellValue(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'string') return isEmptyString(value) ? '—' : value;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function ItemCell({ getValue }: CellContext<CollectionItem, unknown>) {
  const display = formatCellValue(getValue());
  return (
    <Text size="xs" className="fs-mask" truncate title={display}>
      {display}
    </Text>
  );
}

function buildPreviewColumns(items: CollectionItem[]) {
  const firstItem: unknown = items[0];
  // TanStack Table requires a truthy id for an accessorFn column — an empty-string
  // key (e.g. a manually-built collection row like `{ '': 'value' }`) would otherwise
  // fall through to a falsy id and crash getAllColumns/getHeaderGroups.
  const columnKeys =
    firstItem != null && typeof firstItem === 'object'
      ? Object.keys(firstItem)
          .filter((key) => !isEmptyString(key))
          .slice(0, PREVIEW_COLUMNS)
      : [];
  return columnKeys.map((key) =>
    columnHelper.accessor((row) => row[key], {
      id: key,
      header: key,
      cell: ItemCell,
    }),
  );
}

function getImportTypeLabel(importType: string | undefined): string | null {
  if (isEmptyString(importType)) return null;
  return IMPORT_TYPE_LABELS[importType] ?? importType;
}

function getRowsLabel(rowsNumber: string | undefined): string {
  const rowCount = rowsNumber != null ? Number(rowsNumber) : null;
  return rowCount != null && Number.isFinite(rowCount) ? `${formatNumber(rowCount)} ${plur('row', rowCount)}` : '—';
}

type CollectionSampleProps = {
  items: CollectionItem[];
  columns: ReturnType<typeof buildPreviewColumns>;
  isLoading: boolean;
};

function CollectionSample({ items, columns, isLoading }: CollectionSampleProps) {
  return (
    <>
      {isLoading ? (
        <Flex alignItems="center" justifyContent="center" w="full" py="200">
          <Spinner color="information" />
        </Flex>
      ) : (
        <DataTable caption="Collection items" data={items} columns={columns} />
      )}
      <Divider mt="100" />
    </>
  );
}

export function CollectionPreview({ result }: Props) {
  const envId = Number(result.other.env_id);
  const collectionId = Number(result.other.id);
  const { import_type, rows_number, updated_at } = result.other;

  const { data, isLoading } = useHydraUIAPIQuery({
    queryKey: ['collectionItems', result.other.env_id, result.other.id],
    queryFn: async ({ signal }): Promise<CollectionItemsResponse> => {
      // The generated OpenAPI types under-document this endpoint — services accepts
      // collection_id/limit/page (see services ui_api/collections/query.go CollectionItems),
      // but the spec types `query` as `never`, so we pass them through a localized cast.
      // Gated by the same Journeys_Collections_View permission as the collection page the
      // result links to, so this surfaces nothing the user can't already open.
      const response = await hydraUiApi.collections.getCollectionItems({
        params: {
          path: { environment_id: String(envId) },
          query: { collection_id: collectionId, limit: PREVIEW_ROWS, page: 1 },
        },
        signal,
      } as unknown as Parameters<typeof hydraUiApi.collections.getCollectionItems>[0]);
      return response as CollectionItemsResponse;
    },
    queryConfig: { enabled: envId > 0 && collectionId > 0, staleTime: 5 * 60 * 1000 },
  });

  const items = (data?.collection_items ?? []).slice(0, PREVIEW_ROWS);
  const columns = buildPreviewColumns(items);
  // TanStack Table's getHeaderGroups/getAllColumns crashes on an empty columns array
  // (a manually-built collection can have rows with no fields), so only render the
  // table once we actually have columns to show.
  const showSample = isLoading || (items.length > 0 && columns.length > 0);
  const importTypeLabel = getImportTypeLabel(import_type);

  return (
    <>
      {showSample ? <CollectionSample items={items} columns={columns} isLoading={isLoading} /> : null}

      <Flex direction="column" gap="100" w="full" mt={showSample ? '100' : undefined}>
        {importTypeLabel !== null ? (
          <MetadataRow label="Import type">
            <Text size="xs">{importTypeLabel}</Text>
          </MetadataRow>
        ) : null}
        <MetadataRow label="Rows">
          <Text size="xs" className="fs-mask">
            {getRowsLabel(rows_number)}
          </Text>
        </MetadataRow>
        <MetadataRow label="Updated">
          <Text size="xs">
            <FormattedDateTime className="fs-mask" date={updated_at} format="medium" />
          </Text>
        </MetadataRow>
      </Flex>
    </>
  );
}
